import { IPaymentCategory } from 'constants/payment-categories.constants';

/**
 * Deep clone a category tree
 */
export const cloneCategories = (categories: IPaymentCategory[]): IPaymentCategory[] => {
	return categories.map((cat) => ({
		...cat,
		children: cat.children ? cloneCategories(cat.children) : [],
	}));
};

/**
 * Find a category by ID in a tree
 */
export const findCategoryById = (categories: IPaymentCategory[], id: number): IPaymentCategory | null => {
	for (const cat of categories) {
		if (cat.id === id) return cat;
		if (cat.children) {
			const found = findCategoryById(cat.children, id);
			if (found) return found;
		}
	}
	return null;
};

/**
 * Update a category in a tree by ID
 */
export const updateCategoryInTree = (
	categories: IPaymentCategory[],
	id: number,
	updates: Partial<IPaymentCategory>
): IPaymentCategory[] => {
	return categories.map((cat) => {
		if (cat.id === id) {
			return { ...cat, ...updates };
		}
		if (cat.children) {
			return {
				...cat,
				children: updateCategoryInTree(cat.children, id, updates),
			};
		}
		return cat;
	});
};

/**
 * Add a category to a tree under a parent
 */
export const addCategoryToTree = (
	categories: IPaymentCategory[],
	parentId: number | null,
	newCategory: IPaymentCategory
): IPaymentCategory[] => {
	if (parentId === null) {
		return [...categories, newCategory];
	}
	return categories.map((cat) => {
		if (cat.id === parentId) {
			return {
				...cat,
				children: [...(cat.children || []), newCategory],
			};
		}
		if (cat.children) {
			return {
				...cat,
				children: addCategoryToTree(cat.children, parentId, newCategory),
			};
		}
		return cat;
	});
};

/**
 * Get all descendant IDs of a category (including the category itself)
 */
export const getAllDescendantIds = (category: IPaymentCategory): number[] => {
	const ids: number[] = [category.id];
	if (category.children) {
		category.children.forEach((child) => {
			ids.push(...getAllDescendantIds(child));
		});
	}
	return ids;
};

/**
 * Update multiple categories in a tree by IDs
 */
export const updateMultipleCategoriesInTree = (
	categories: IPaymentCategory[],
	ids: number[],
	updates: Partial<IPaymentCategory>
): IPaymentCategory[] => {
	return categories.map((cat) => {
		const shouldUpdate = ids.includes(cat.id);
		const updatedCat = shouldUpdate ? { ...cat, ...updates } : cat;
		if (cat.children) {
			return {
				...updatedCat,
				children: updateMultipleCategoriesInTree(cat.children, ids, updates),
			};
		}
		return updatedCat;
	});
};

